import React, { memo, useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { areEqual } from 'Utils/equalityChecks';
import styles from 'Components/PdfFormTemplates/pdfFormTemplates.module.css';
import { PurpleButton } from 'Components/Button/PurpleButton';
import { firstCompanyIdSelector } from 'Containers/Projects/selectors';
import { PdfFormTemplatesList } from 'Components/PdfFormTemplates/PdfFormTemplates';
import { PdfFormSubmissionsList } from 'Components/PdfFormTemplates/PdfFormSubmissions';
import { CreateTemplateModal } from './CreateTemplateModal';
import { DeleteTemplateModal } from './DeleteTemplateModal';
import { SendFormModal } from './SendFormModal';
import {
  pdfFormTemplatesSelector,
  pdfFormSubmissionsSelector,
  fetchingPdfFormTemplatesSelector,
  pdfFormTemplateCreatedSelector,
  pdfFormTemplateDeletedSelector,
  pdfFormSubmissionCreatedSelector,
  pdfFormSubmissionEmailedSelector,
  pdfFormSubmissionDeletedSelector,
} from './selectors';
import {
  listPdfFormTemplates,
  listPdfFormSubmissions,
  emailPdfFormSubmission,
  deletePdfFormSubmission,
  setTemplateCreated,
  setTemplateDeleted,
  setSubmissionCreated,
  setSubmissionEmailed,
  setSubmissionDeleted,
} from './actions';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

function PdfFormTemplatesContainer() {
  const dispatch = useDispatch();
  const history = useHistory();

  const companyId = useSelector(firstCompanyIdSelector, areEqual);
  const templates = useSelector(pdfFormTemplatesSelector, areEqual);
  const submissions = useSelector(pdfFormSubmissionsSelector, areEqual);
  const fetching = useSelector(fetchingPdfFormTemplatesSelector, areEqual);
  const templateCreated = useSelector(pdfFormTemplateCreatedSelector, areEqual);
  const templateDeleted = useSelector(pdfFormTemplateDeletedSelector, areEqual);
  const submissionCreated = useSelector(pdfFormSubmissionCreatedSelector, areEqual);
  const submissionEmailed = useSelector(pdfFormSubmissionEmailedSelector, areEqual);
  const submissionDeleted = useSelector(pdfFormSubmissionDeletedSelector, areEqual);

  const [activeTab, setActiveTab] = useState<'templates' | 'submissions'>('templates');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isSendOpen, setIsSendOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);

  // Submissions are company-scoped: the backend resolves company_id to the active
  // company, so every refresh has to carry the company currently being viewed.
  const refreshSubmissions = useCallback(() => {
    if (companyId) dispatch(listPdfFormSubmissions(1, undefined, companyId) as any);
  }, [companyId]);

  // Fetch data
  useEffect(() => {
    if (companyId) {
      dispatch(listPdfFormTemplates(companyId) as any);
      refreshSubmissions();
    }
  }, [companyId, refreshSubmissions]);

  // Refresh after template create/delete
  useEffect(() => {
    if (templateCreated) {
      dispatch(listPdfFormTemplates(companyId) as any);
      setIsCreateOpen(false);
      dispatch(setTemplateCreated(false) as any);
    }
  }, [templateCreated, companyId]);

  useEffect(() => {
    if (templateDeleted) {
      dispatch(listPdfFormTemplates(companyId) as any);
      setIsDeleteOpen(false);
      setSelectedTemplate(null);
      dispatch(setTemplateDeleted(false) as any);
    }
  }, [templateDeleted, companyId]);

  // Refresh after submission actions
  useEffect(() => {
    if (submissionCreated) {
      refreshSubmissions();
      dispatch(setSubmissionCreated(false) as any);
    }
  }, [submissionCreated, refreshSubmissions]);

  useEffect(() => {
    if (submissionEmailed) {
      refreshSubmissions();
      dispatch(setSubmissionEmailed(false) as any);
    }
  }, [submissionEmailed, refreshSubmissions]);

  useEffect(() => {
    if (submissionDeleted) {
      refreshSubmissions();
      dispatch(setSubmissionDeleted(false) as any);
    }
  }, [submissionDeleted, refreshSubmissions]);

  // Template handlers
  const onCreateClick = useCallback(() => setIsCreateOpen(true), []);
  const onCreateClose = useCallback(() => setIsCreateOpen(false), []);

  const onEditClick = useCallback(
    (template: any) => {
      history.push(`/pdf-forms/${template.id}/editor`);
    },
    [history]
  );

  const onDeleteClick = useCallback((template: any) => {
    setSelectedTemplate(template);
    setIsDeleteOpen(true);
  }, []);

  const onDeleteClose = useCallback(() => {
    setIsDeleteOpen(false);
    setSelectedTemplate(null);
  }, []);

  const onSendClick = useCallback((template: any) => {
    setSelectedTemplate(template);
    setIsSendOpen(true);
  }, []);

  const onSendClose = useCallback(() => {
    setIsSendOpen(false);
    setSelectedTemplate(null);
  }, []);

  // Submission handlers
  const onResendSubmission = useCallback(
    (sub: any) => {
      if (sub?.id && sub?.client_email) {
        dispatch(emailPdfFormSubmission(sub.id, sub.client_email) as any);
      }
    },
    [dispatch]
  );

  const onDeleteSubmission = useCallback(
    (sub: any) => {
      if (sub?.id && window.confirm(`Delete submission for ${sub.client_name}?`)) {
        dispatch(deletePdfFormSubmission(sub.id) as any);
      }
    },
    [dispatch]
  );

  const [viewingPdfUrl, setViewingPdfUrl] = useState<string | null>(null);
  const [pdfNumPages, setPdfNumPages] = useState(1);
  const [pdfCurrentPage, setPdfCurrentPage] = useState(1);

  const onViewPdf = useCallback((url: string) => {
    setViewingPdfUrl(url);
    setPdfCurrentPage(1);
    setPdfNumPages(1);
  }, []);

  const onClosePdfViewer = useCallback(() => {
    setViewingPdfUrl(null);
  }, []);

  const onDownloadPdf = useCallback(async () => {
    if (!viewingPdfUrl) return;
    try {
      const response = await fetch(viewingPdfUrl);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'document.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // CORS blocked or network error — redirect to URL directly so the browser handles it
      window.location.href = viewingPdfUrl;
    }
  }, [viewingPdfUrl]);

  const onTabClick = useCallback((e: any) => {
    const tabId = e?.currentTarget?.id;
    if (tabId === 'templates-tab') setActiveTab('templates');
    else if (tabId === 'submissions-tab') setActiveTab('submissions');
  }, []);

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col">
          <div className={styles.pageWrapper}>
            <div className={styles.tabsContainer}>
              <ul className={`nav nav-tabs ${styles.tabs}`} role="tablist">
                <li className="nav-item" role="presentation">
                  <button
                    type="button"
                    id="templates-tab"
                    className={`nav-link ${styles.tab} ${styles.tabButton} ${activeTab === 'templates' ? `active ${styles.activeTab}` : ''}`}
                    role="tab"
                    aria-selected={activeTab === 'templates'}
                    aria-controls="templates-panel"
                    onClick={onTabClick}
                  >
                    Templates
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    type="button"
                    id="submissions-tab"
                    className={`nav-link ${styles.tab} ${styles.tabButton} ${activeTab === 'submissions' ? `active ${styles.activeTab}` : ''}`}
                    role="tab"
                    aria-selected={activeTab === 'submissions'}
                    aria-controls="submissions-panel"
                    onClick={onTabClick}
                  >
                    Submissions
                  </button>
                </li>
              </ul>
            </div>

            {activeTab === 'templates' && (
              <div className={styles.addButtonRow}>
                <PurpleButton className={styles.addButton} onClick={onCreateClick}>
                  Add +
                </PurpleButton>
              </div>
            )}

            <div className="tab-content w-100" id={`${activeTab}-panel`} role="tabpanel" aria-labelledby={`${activeTab}-tab`}>
              {activeTab === 'templates' ? (
                <PdfFormTemplatesList
                  templates={templates}
                  fetching={fetching}
                  onEditClick={onEditClick}
                  onDeleteClick={onDeleteClick}
                  onSendClick={onSendClick}
                />
              ) : (
                <PdfFormSubmissionsList
                  submissions={submissions}
                  fetching={fetching}
                  onResend={onResendSubmission}
                  onDelete={onDeleteSubmission}
                  onViewPdf={onViewPdf}
                />
              )}
            </div>

            {/* Render modals via portal to keep them outside the main content DOM */}
            {ReactDOM.createPortal(
              <>
                <CreateTemplateModal isOpen={isCreateOpen} onClose={onCreateClose} />
                <DeleteTemplateModal isOpen={isDeleteOpen} template={selectedTemplate} onClose={onDeleteClose} />
                <SendFormModal isOpen={isSendOpen} template={selectedTemplate} onClose={onSendClose} />

                {/* Inline PDF Viewer Modal */}
                {viewingPdfUrl && (
                  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                  <div className={styles.pdfViewerOverlay} onClick={onClosePdfViewer}>
                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                    <div className={styles.pdfViewerModal} onClick={(e) => e.stopPropagation()}>
                      <div className={styles.pdfViewerHeader}>
                        <div className={styles.pdfViewerNav}>
                          {pdfNumPages > 1 && (
                            <>
                              <button
                                type="button"
                                disabled={pdfCurrentPage <= 1}
                                onClick={() => setPdfCurrentPage((p) => p - 1)}
                              >
                                ←
                              </button>
                              <span>{`Page ${pdfCurrentPage} of ${pdfNumPages}`}</span>
                              <button
                                type="button"
                                disabled={pdfCurrentPage >= pdfNumPages}
                                onClick={() => setPdfCurrentPage((p) => p + 1)}
                              >
                                →
                              </button>
                            </>
                          )}
                        </div>
                        <div className={styles.pdfViewerActions}>
                          <button type="button" className={styles.pdfDownloadBtn} onClick={onDownloadPdf}>
                            Download PDF
                          </button>
                          <button type="button" className={styles.pdfCloseBtn} onClick={onClosePdfViewer}>
                            ✕
                          </button>
                        </div>
                      </div>
                      <div className={styles.pdfViewerBody}>
                        <Document
                          file={viewingPdfUrl}
                          onLoadSuccess={({ numPages: total }) => setPdfNumPages(total)}
                          loading={<div style={{ padding: 40, textAlign: 'center' }}>Loading PDF...</div>}
                        >
                          <Page
                            pageNumber={pdfCurrentPage}
                            scale={1}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                            width={Math.min(700, window.innerWidth * 0.85 - 32)}
                          />
                        </Document>
                      </div>
                    </div>
                  </div>
                )}
              </>,
              document.body
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const PdfFormTemplatesContainerMemo = memo(PdfFormTemplatesContainer, areEqual);
export { PdfFormTemplatesContainerMemo as PdfFormTemplatesContainer };
